import React, { useCallback, useEffect, useState } from "react";
import {
  Header,
  Segment,
  Table,
  Loader,
  Select,
  Button,
  Icon,
  Confirm,
} from "semantic-ui-react";
import { useFilters, notEmptyValidator } from "../../../../../hooks";
import {
  getTasksApi,
  redoTaskApi,
  redoDocumentApi,
  markAsPrintedApi,
} from "../../../../../apis/tasksApi";
import { usePagination } from "../../../../../hooks/usePagination.hook";
import toastr from "toastr";
import moment from "moment";

const TASK_STATUSES = [
  { key: "idle", text: "Idle", value: "idle" },
  { key: "processed", text: "Processed", value: "processed" },
  { key: "pending", text: "Pending", value: "pending" },
  { key: "printed", text: "Printed", value: "printed" },
  { key: "errored", text: "Errored", value: "errored" },
];

const columns = [
  { label: "Task ID", prop: "id", width: 3 },
  { label: "Type", prop: "taskType", width: 1 },
  { label: "Status", prop: "status", width: 1 },
  { label: "Documents", prop: "dsPdfUrl", width: 1 },
  { label: "Pictures", prop: "picturesUrl", width: 1 },
  { label: "Shipping", prop: "shippingLabelsUrl", width: 1 },
  { label: "Created At", prop: "createdAt", width: 2 },
  { label: "Redo", prop: "redo", width: 1 },
  { label: "Update Status", prop: "updateStatus", width: 1 },
];

const TasksPage = ({ processingState }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [loadingActions, setLoadingActions] = useState({});

  const { filters, normalizedFilters, setFormFilter } = useFilters(
    {
      taskStatus: "processed",
      processingState: processingState || "",
    },
    {
      localStorageKey: `tasks-${processingState || 'all'}`,
      filterValidators: {
        taskStatus: notEmptyValidator(),
      },
      temporaryFilters: ["taskStatus"],
    },
  );

  const {
    items: tasks,
    Paginator,
    loadItems: loadData,
    setItems: setTasks,
    isLoading,
  } = usePagination(getTasksApi, {
    initialLoad: false,
    clearOnLoad: false,
    dataProperty: "tasks",
    limit: 10
  });

  useEffect(() => {
    loadData(1, normalizedFilters);
  }, [normalizedFilters, loadData, setTasks]);

  const handleRedoTask = async (taskId) => {
    try {
      setLoadingActions((prev) => ({ ...prev, [`redoTask_${taskId}`]: true }));
      await redoTaskApi(taskId);
      loadData(1, normalizedFilters);
      toastr.success(
        "The task is being redone. Please check the status after a few seconds to see if it is idle, pending, or processed.",
      );
    } catch (error) {
      console.error("Error redoing task:", error);
      toastr.error("Failed to redo task. Please try again.");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`redoTask_${taskId}`]: false }));
    }
  };

  const handleRedoDocuments = async (taskId) => {
    try {
      setLoadingActions((prev) => ({
        ...prev,
        [`redoDocuments_${taskId}`]: true,
      }));
      await redoDocumentApi(taskId);
      loadData(1, normalizedFilters);
      toastr.success(
        "The documents are being redone. Please check the status after a few seconds to see if it is idle, pending, or processed.",
      );
    } catch (error) {
      console.error("Error redoing documents:", error);
      toastr.error("Failed to redo documents. Please try again.");
    } finally {
      setLoadingActions((prev) => ({
        ...prev,
        [`redoDocuments_${taskId}`]: false,
      }));
    }
  };

  const handleMarkAsPrinted = async (taskId) => {
    try {
      setLoadingActions((prev) => ({
        ...prev,
        [`markAsPrinted_${taskId}`]: true,
      }));
      const applicationTokens = tasks
        .find((task) => task.id === taskId)
        .applications.map((app) => app.form.applicationToken);
      await markAsPrintedApi(taskId, applicationTokens);
      loadData(1, normalizedFilters);
      toastr.success("Task has been successfully marked as printed!");
    } catch (error) {
      console.error("Error marking as printed:", error);
      toastr.error("Failed to mark task as printed. Please try again.");
    } finally {
      setLoadingActions((prev) => ({
        ...prev,
        [`markAsPrinted_${taskId}`]: false,
      }));
    }
  };

  const renderHeader = useCallback(() => {
    const tabTitle = processingState === "PR" 
      ? "Tasks - PKO" 
      : processingState === "PA" 
        ? "Tasks - PKO (US)" 
        : "Tasks";

    return (
      <Segment className="tableTitle adminTable" inverted>
        <Header as="h1">{tabTitle}</Header>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Select
            placeholder="Select status"
            options={TASK_STATUSES}
            value={filters.taskStatus}
            name="taskStatus"
            onChange={setFormFilter}
          />
        </div>
      </Segment>
    );
  }, [filters.taskStatus, setFormFilter, processingState]);

  const renderExpandedContent = (task) => (
    <Table.Row>
      <Table.Cell colSpan={columns.length}>
        <div style={{ display: "flex", gap: "2rem" }}>
          <div
            style={{
              flex: "0 0 25%",
              borderRight: "1px solid var(--border-color, #ccc)",
              paddingRight: "1rem",
            }}
          >
            <h4>Applications</h4>
            {task.applications?.map((app) => (
              <div
                key={app.id}
                style={{
                  marginBottom: "1rem",
                  border: "1px solid var(--border-color, #ccc)",
                  padding: "1rem",
                  borderRadius: "5px",
                  backgroundColor: "var(--card-bg, #fff)",
                  color: "var(--text-color, inherit)",
                }}
              >
                <div>
                  <a
                    href={`${app.form.formType}/applicant/${app.form.data._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`${app.form.data.firstName} ${app.form.data.lastName}(${app.form.data.userEmail})`}
                    style={{
                      textDecoration: "underline",
                      color: "var(--link-color, blue)",
                      fontWeight: "bold",
                    }}
                  >
                    {app.form.data.firstName} {app.form.data.lastName} &nbsp;(
                    {app.form.data.userEmail})
                  </a>
                  <br />
                  {app.form.data.trackingNumber
                    ? `Tracking Number: ${app.form.data.trackingNumber}`
                    : ""}
                </div>
              </div>
            ))}
          </div>
          <div style={{ flex: "0 0 73%" }}>
            <Segment>
              <h4>Errors</h4>
              {task.errors &&
                Object.keys(task.errors).map((errorKey) => (
                  <div
                    key={errorKey}
                    style={{
                      marginBottom: "1rem",
                      padding: "1rem",
                      border: "1px solid var(--border-color, #ccc)",
                      borderRadius: "5px",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    <strong>{`${
                      errorKey.charAt(0).toUpperCase() + errorKey.slice(1)
                    } Error:`}</strong>
                    <pre
                      style={{
                        margin: "0",
                        padding: "0.5rem",
                        borderRadius: "3px",
                        whiteSpace: "pre-wrap",
                        border: "1px solid var(--border-color, #ddd)",
                      }}
                    >
                      {task.errors[errorKey].message}
                    </pre>
                    <pre
                      style={{
                        margin: "0.5rem 0 0",
                        padding: "0.5rem",
                        borderRadius: "3px",
                        whiteSpace: "pre-wrap",
                        border: "1px solid var(--border-color, #ddd)",
                      }}
                    >
                      {task.errors[errorKey].stack}
                    </pre>
                  </div>
                ))}
            </Segment>
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );

  const renderTable = useCallback(() => {
    if (isLoading) {
      return <Loader active />;
    }

    return (
      <>
        <Table celled>
          <Table.Header>
            <Table.Row>
              {columns.map(({ label, width }) => (
                <Table.HeaderCell key={label} width={width}>
                  {label}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tasks && tasks.length > 0 ? (
              tasks.map((task) => (
                <React.Fragment key={task.id}>
                  <Table.Row>
                    <Table.Cell
                      onClick={() =>
                        setExpandedRows((prev) => ({
                          ...prev,
                          [task.id]: !prev[task.id],
                        }))
                      }
                      style={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <Icon
                        name={
                          expandedRows[task.id]
                            ? "chevron down"
                            : "chevron right"
                        }
                        style={{ cursor: "pointer", marginRight: "1rem" }}
                      />
                      {task.id}
                    </Table.Cell>
                    <Table.Cell style={{ textTransform: "uppercase" }}>
                      {task.applications.length > 0
                        ? task.applications[0].form.formType
                        : "UNKOWN"}
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        textTransform: "uppercase",
                        backgroundColor:
                          task.status === "errored" ? "red" : "transparent",
                        color: task.status === "errored" ? "white" : "inherit",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {task.status}
                    </Table.Cell>
                    <Table.Cell>
                      {task.dsPdfUrl && (
                        <a
                          href={task.dsPdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Document
                        </a>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {task.picturesUrl && (
                        <a
                          href={task.picturesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Pictures
                        </a>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {task.shippingLabelsUrl && (
                        <a
                          href={task.shippingLabelsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Shipping Labels
                        </a>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {moment(task.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                    </Table.Cell>
                    <Table.Cell>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                        }}
                      >
                        <Button
                          size="tiny"
                          loading={loadingActions[`redoTask_${task.id}`]}
                          disabled={loadingActions[`redoTask_${task.id}`]}
                          onClick={() =>
                            setConfirmAction({ type: "redoTask", id: task.id })
                          }
                        >
                          Redo Task
                        </Button>
                        <Button
                          size="tiny"
                          loading={loadingActions[`redoDocuments_${task.id}`]}
                          disabled={loadingActions[`redoDocuments_${task.id}`]}
                          onClick={() =>
                            setConfirmAction({
                              type: "redoDocuments",
                              id: task.id,
                            })
                          }
                        >
                          Redo Documents
                        </Button>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {task.status !== "printed" ? (
                        <Button
                          size="small"
                          loading={loadingActions[`markAsPrinted_${task.id}`]}
                          disabled={loadingActions[`markAsPrinted_${task.id}`]}
                          onClick={() =>
                            setConfirmAction({
                              type: "markAsPrinted",
                              id: task.id,
                            })
                          }
                        >
                          Mark as Printed
                        </Button>
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                  {expandedRows[task.id] && renderExpandedContent(task)}
                </React.Fragment>
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={columns.length + 1}>
                  No tasks available.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={columns.length + 1}>
                <Paginator />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>

        <Confirm
          open={!!confirmAction}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => {
            if (!confirmAction) return;

            switch (confirmAction.type) {
              case "redoTask":
                handleRedoTask(confirmAction.id);
                break;
              case "redoDocuments":
                handleRedoDocuments(confirmAction.id);
                break;
              case "markAsPrinted":
                handleMarkAsPrinted(confirmAction.id);
                break;
            }
            setConfirmAction(null);
          }}
          content={
            confirmAction?.type === "redoTask"
              ? "Are you sure you want to redo this task?"
              : confirmAction?.type === "redoDocuments"
              ? "Are you sure you want to redo the documents?"
              : "Are you sure you want to mark this as printed?"
          }
        />
      </>
    );
  }, [
    isLoading,
    tasks,
    Paginator,
    expandedRows,
    confirmAction,
    loadingActions,
  ]);

  return (
    <>
      <Segment.Group style={{ display: "table", width: "100%" }}>
        {renderHeader()}
        {renderTable()}
      </Segment.Group>
    </>
  );
};

export default TasksPage;
